import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import dataService from '../services/dataService';
import bluetoothService from '../services/bluetoothService';
import sensorService from '../services/sensorService';

const DataContext = createContext();

export const useData = () => useContext(DataContext);


export const DataProvider = ({ children }) => {
  const [sites, setSites] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedSites, setSelectedSites] = useState({});
  const [markers, setMarkers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [otherData, setOtherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Bluetooth-related state variables
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [isBluetoothConnected, setIsBluetoothConnected] = useState(false);
  const [bluetoothSerialOutput, setBluetoothSerialOutput] = useState('');
  
  // Configuration and measurement-related state variables
  const [config, setConfig] = useState({
    connectionType: 'serial',
    port: null,
    baudRate: 115200,
    surface_distance: 0.5,
    sensor_angle: 45.0,
    threshold_sensitivity: 0.15,
    psd_lp_coeff: 0.75,
    velocity_lp_coeff: 0.98,
    sweep_rate: 3000,
    frame_rate: 10.0,
    cfar_guard: 6,
    cfar_win: 6,
    max_peak_interval_s: 4.0,
    time_series_length: 512,
    slow_zone_half_length: 3,
    report_interval_ms: 1000,
    hwaas: 16,
    num_points: 4,
    step_length: 1,
    sweeps_per_frame: 128,
    continuous_sweep_mode: true,
    double_buffering: true,
    anomalyDetectionMethod: 'ON', // Anomaly detection method
    anomalyDetectionWindow: 20,    // Number of recent measurements to consider for anomaly detection
    distanceChangeThreshold: 3,           // Threshold for anomaly detection
    velocityChangeThreshold: 2     // Velocity change threshold (m/s^2) for rapid change detection
  });
  
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [availablePorts, setAvailablePorts] = useState([]);
  const [hasPortPermission, setHasPortPermission] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [serialData, setSerialData] = useState('');



  const updateChartData = useCallback((newData) => {
    setChartData(newData);
  }, []);
  
  const requestPortPermissionAndList = async () => {
    try {
      const ports = await sensorService.requestPortAndList();
      setAvailablePorts(ports);
      setHasPortPermission(ports.length > 0);
      return ports.length > 0;
    } catch (error) {
      console.error('Failed to request port permission and list:', error);
      setHasPortPermission(false);
      setError('Failed to get port access. Please try again.');
      return false;
    }
  };

  const handlePortSelect = useCallback(async (selectedPortInfo) => {
    if (!selectedPortInfo) {
      setConfig(prevConfig => ({ ...prevConfig, port: null }));
      return true;
    }

    try {
      const selectedPort = await sensorService.selectPort(selectedPortInfo);
      if (!selectedPort) {
        throw new Error('Failed to select port: Port not found');
      }

      setConfig(prevConfig => ({
        ...prevConfig,
        port: {
          ...selectedPortInfo,
          serialPort: selectedPort
        }
      }));
      console.log('Port selected and stored in context:', selectedPort);
      return true;
    } catch (error) {
      console.error('Failed to select port:', error);
      setError('Failed to select port. Please try again.');
      setConfig(prevConfig => ({ ...prevConfig, port: null }));
      return false;
    }
  }, []);



  const clearSerialData = () => {
    setSerialData('');  // Clear the serial data
  };

const startConfigurePort = async () => {
  if (!config.port) {
    setError('No port selected. Please select a port before starting measurement.');
    return;
  }

  try {
    setChartData([]);
    setSerialData('');
    setIsConfiguring(true);
    setIsMeasuring(true);
    setError(null);

    if (!sensorService.isPortOpen()) {
      await sensorService.openPort(config.port, config.baudRate);
    }

    console.log('Starting port configuration...');
    setSerialData('Port opened successfully.\n');

    // Send an initial command to "wake up" the port
    console.log('Sending initial enter command');
    await sensorService.writeData(new TextEncoder().encode('\r\n'));
    setSerialData(prevData => prevData + 'Sent initial enter command.\n');
    await sensorService.delay(500);

    // Define the default configuration
    const defaultConfig = {
      connectionType: 'serial',
      port: null,
      baudRate: 115200,
      surface_distance: 0.5,
      sensor_angle: 45.0,
      threshold_sensitivity: 0.15,
      psd_lp_coeff: 0.75,
      velocity_lp_coeff: 0.98,
      sweep_rate: 3000,
      frame_rate: 10.0,
      cfar_guard: 6,
      cfar_win: 6,
      max_peak_interval_s: 4.0,
      time_series_length: 512,
      slow_zone_half_length: 3,
      hwaas: 16,
      num_points: 4,
      step_length: 1,
      sweeps_per_frame: 128,
      report_interval_ms: 1000, 
      continuous_sweep_mode: true,
      double_buffering: true
    };

    // Exclude certain keys from the comparison
    const excludedKeys = [
      'connectionType', 
      'port', 
      'baudRate', 
      'anomalyDetectionWindow', 
      'distanceChangeThreshold',
      'anomalyDetectionMethod',
      'velocityChangeThreshold'
    ];

    const changedConfig = Object.keys(config).reduce((acc, key) => {
      if (excludedKeys.includes(key)) return acc;
      if (config[key] !== defaultConfig[key]) {
        acc[key] = config[key];
      }
      return acc;
    }, {});

    const configCommand = Object.entries(changedConfig)
      .map(([key, value]) => `${key}=${value}`)
      .join(',');

    if (configCommand.length > 0) {
      console.log(`Sending configuration: ${configCommand.trim()}`);
      await sensorService.writeData(new TextEncoder().encode(configCommand + '\r\n'));
      setSerialData(prevData => prevData + `Sent configuration: ${configCommand.trim()}\n`);
      await sensorService.delay(500);
    } else {
      console.log('No configuration changes from default.');
      setSerialData(prevData => prevData + 'No configuration changes from default.\n');
    }

    console.log('Sending start command...');
    await sensorService.writeData(new TextEncoder().encode('start\r\n'));
    console.log('Start command sent');
    setSerialData(prevData => prevData + 'Start command sent. Measurement beginning...\n');

    await new Promise(resolve => setTimeout(resolve, 500));

    sensorService.isReading = true;
    sensorService.isStopping = false;

    while (sensorService.isReading && sensorService.isPortOpen()) {
      try {
        await sensorService.readData((data) => {
          setSerialData(prevData => prevData + data + '\n');

          const decodedData = decodeMeasurementData(data, config);

          if (decodedData !== null) {
            setChartData(prevData => {
              const newData = [
                ...prevData.slice(-999),
                { velocity: decodedData.velocity, distance: decodedData.distance }
              ];

              return newData;
            });
          }
        });
      } catch (readError) {
        if (sensorService.isStopping) {
          console.log('Stopping measurement as requested');
          break;
        }
        console.error('Error reading data:', readError);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  } catch (error) {
    console.error('Failed to configure and start measurement:', error);
    setError(`Failed to configure and start measurement: ${error.message}`);
  } finally {
    setIsConfiguring(false);
    setIsMeasuring(false);
  }
};

  const handleChange = useCallback((name, value) => {
    setConfig(prevConfig => ({ ...prevConfig, [name]: value }));
  }, []);

  const handleStartMeasurement = async () => {
    if (!config.port) {
      setError('No port selected. Please select a port before starting measurement.');
      return;
    }
  
    try {
      setChartData([]);
      setSerialData('');
      setIsMeasuring(true);
      setError(null);
      
      if (!sensorService.isPortOpen()) {
        await sensorService.openPort(config.port, config.baudRate);
      }
  
      console.log('Sending start command...');
      await sensorService.writeData(new TextEncoder().encode('start\r\n'));
      console.log('Start command sent');
  
      await new Promise(resolve => setTimeout(resolve, 500));
  
      sensorService.isReading = true;
      sensorService.isStopping = false;
  
      while (sensorService.isReading && sensorService.isPortOpen()) {
        try {
          await sensorService.readData((data) => {
            setSerialData(prevData => prevData + data + '\n');
  
            const decodedData = decodeMeasurementData(data, config);
  
            if (decodedData !== null) {
              setChartData(prevData => {
                const newData = [
                  ...prevData.slice(-199),
                  { velocity: decodedData.velocity, distance: decodedData.distance }
                ];

                return newData;
              });
            }
          });
        } catch (readError) {
          if (sensorService.isStopping) {
            console.log('Stopping measurement as requested');
            break;
          }
          console.error('Error reading data:', readError);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    } catch (error) {
      console.error('Failed to start measurement:', error);
      setError(`Failed to start measurement: ${error.message}`);
    } finally {
      setIsMeasuring(false);
    }
  };
  
  const handleStop = async () => {
    try {
      console.log('Stopping measurement...');
      
      await sensorService.stopMeasurement();
      
      // Attempt to close the port, but don't throw an error if it fails
      try {
        if (sensorService.isPortOpen()) {
          await sensorService.closePort();
        }
      } catch (closeError) {
        console.warn('Error closing port:', closeError);
      }
      
      setIsMeasuring(false);
      console.log('Measurement stopped and data cleared');
    } catch (error) {
      console.error('Failed to stop measurement:', error);
      setError('Failed to stop measurement. Please refresh the page and try again.');
    } finally {
      // Ensure these states are reset even if an error occurs
      setIsMeasuring(false);
      sensorService.isReading = false;
      sensorService.isStopping = false;
    }
  };

  const recentMeasurements = [];

  function decodeMeasurementData(data, config) {
    // console.log('Input data:', data);
  
    // Updated regex to be more flexible with whitespace and units
    const measurementRegex = /V:\s*([-]?\d+(?:\.\d+)?)\s*(?:m\/s)?,\s*D:\s*([-]?\d+(?:\.\d+)?)\s*(?:m)?/;
    const match = measurementRegex.exec(data);
  
    if (match) {

      const [, velocityStr, distanceStr] = match;
      const velocity = parseFloat(velocityStr);
      const distance = parseFloat(distanceStr);

  
      // Check if the data is valid and not an anomaly
      if (!isAnomaly(velocity, distance, config)) {

        return  {velocity, distance}; // Return only the distance as a float
      } else {
        console.warn('Anomaly detected:', { velocity, distance });
        return null;
      }
    } else {
      console.log('No regex match');
    }
  
    return null;
  }
  
  function isAnomaly(velocity, distance, config) {
    if (config.anomalyDetectionMethod === 'OFF') {
      return false; // If anomaly detection is off, always return false
    }
  
    if (recentMeasurements.length === 0) {
      return false; // If no previous measurements, cannot determine anomaly
    }
  
    // Get the last recorded velocity and distance
    const lastMeasurement = recentMeasurements[recentMeasurements.length - 1];
    const lastVelocity = lastMeasurement.velocity;
    const lastDistance = lastMeasurement.distance;
  
    // Calculate the changes
    const velocityChange = Math.abs(velocity - lastVelocity);
    const distanceChange = Math.abs(distance - lastDistance);
  
    // Check if changes exceed the thresholds
    if (velocityChange > config.velocityChangeThreshold || distanceChange > config.distanceChangeThreshold) {
      console.warn('Anomaly detected due to rapid change:', { velocityChange, distanceChange });
      return true;
    }
  
    // If no anomaly, return false
    return false;
  }
  
  // Fetch devices and sites
  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const devicesData = await dataService.listDevices();

      const processedDevices = [];
      const processedSites = [];

      devicesData.forEach(item => {
        if (item.attributes && item.attributes.id) {
          processedSites.push({
            name: item.name,
            id: item.attributes.id,
            location: item.attributes.Location,
            latitude: item.attributes.latitude,
            longitude: item.attributes.longitude,
            TypeofMeasurements: item.attributes.TypeofMeasurements,
            unit1: item.attributes.unit1,
            unit2: item.attributes.unit2,
          });

          processedDevices.push({
            name: item.name,
            ...item.attributes,
          });
        }
      });

      setDevices(processedDevices);
      setSites(processedSites);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const flattenSites = useMemo(() => {
    const flattened = [];
    const flatten = (items) => {
      items.forEach(item => {
        flattened.push(item);
        if (item.children && item.children.length > 0) {
          flatten(item.children);
        }
      });
    };
    flatten(sites);
    return flattened;
  }, [sites]);

  useEffect(() => {
    const selectedList = Object.keys(selectedSites).filter(id => selectedSites[id]);
    const pins = flattenSites.filter(station => selectedList.includes(station.id));
    setMarkers(pins);
  }, [selectedSites]);

  const onSelectSite = (selectedKeys) => {
    const newSelectedSites = { ...selectedSites };
    selectedKeys.forEach(key => {
      if (!key.includes('-')) {
        newSelectedSites[key] = !newSelectedSites[key];
      }
    });
    setSelectedSites(newSelectedSites);
  };

  const onSearch = (value) => {
    setSearchValue(value);
  };

  const addDevice = async (deviceInfo) => {
    try {
      const newDevice = await dataService.createDevice(deviceInfo);
      setDevices(prevDevices => [...prevDevices, newDevice]);
      if (newDevice.attributes && newDevice.attributes.Location) {
        setSites(prevSites => [...prevSites, newDevice]);
      }
    } catch (error) {
      console.error('Error adding device:', error);
      throw error;
    }
  };

  const updateDevice = async (thingName, deviceInfo) => {
    try {
      const updatedDevice = await dataService.updateDevice(thingName, deviceInfo);
      setDevices(prevDevices =>
        prevDevices.map(device =>
          device.name === thingName ? updatedDevice : device
        )
      );
      if (updatedDevice.attributes && updatedDevice.attributes.Location) {
        setSites(prevSites =>
          prevSites.map(site =>
            site.name === thingName ? updatedDevice : site
          )
        );
      }
    } catch (error) {
      console.error('Error updating device:', error);
      throw error;
    }
  };

  const deleteDevice = async (thingName) => {
    try {
      await dataService.deleteDevice(thingName);
      setDevices(prevDevices => prevDevices.filter(device => device.name !== thingName));
      setSites(prevSites => prevSites.filter(site => site.name !== thingName));
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  };

  // Bluetooth methods
  const connectBluetooth = async () => {
    try {
      const device = await bluetoothService.requestDevice();
      await bluetoothService.connectToDevice();
      setBluetoothDevice(device);
      setIsBluetoothConnected(true);

      // Set up disconnection listener to update state
      device.addEventListener('gattserverdisconnected', () => {
        setIsBluetoothConnected(false);
        setBluetoothDevice(null);
        setBluetoothSerialOutput(''); // Clear the serial output on disconnect
      });

      // Start receiving serial output notifications
      await bluetoothService.startNotifications((value) => {
        setBluetoothSerialOutput((prev) => prev + value + '\n');
      });
    } catch (error) {
      console.error('Error connecting to Bluetooth device:', error);
      setError('Failed to connect to Bluetooth device. Please try again.');
    }
  };

  const disconnectBluetooth = async () => {
    try {
      await bluetoothService.disconnect();
      setBluetoothDevice(null);
      setIsBluetoothConnected(false);
      setBluetoothSerialOutput(''); // Clear the serial output on disconnect
    } catch (error) {
      console.error('Error disconnecting from Bluetooth device:', error);
    }
  };

  const sendWiFiCredentialsBluetooth = async (ssid, password) => {
    try {
      // Prepare the message to be sent
      const messageToSend = `Sending Wi-Fi credentials: SSID=${ssid}, Password=${password}`;

      // Update the bluetoothSerialOutput with the message being sent
      setBluetoothSerialOutput(prev => prev + messageToSend + '\n');

      await bluetoothService.sendWiFiCredentials(ssid, password, (progress) => {
        setBluetoothSerialOutput(prev => prev + progress);
      });

      console.log('Wi-Fi credentials sent successfully');
    } catch (error) {
      console.error('Error sending Wi-Fi credentials:', error);
      setError('Failed to send Wi-Fi credentials. Please try again.');
    }
  };

  const value = {
    // Existing values
    config,
    setConfig,
    chartData,
    updateChartData,  // Add this to the context value
    availablePorts,
    hasPortPermission,
    isMeasuring,
    requestPortPermissionAndList,
    handleChange,
    handlePortSelect,
    handleStartMeasurement,
    handleStop,
    sites,
    devices,
    selectedSites,
    markers,
    searchValue,
    onSelectSite,
    onSearch,
    setSites,
    setDevices,
    clearSerialData,  // Include the clear function
    otherData,
    setOtherData,
    loading,
    error,
    flattenSites,
    addDevice,
    updateDevice,
    deleteDevice,
    // Bluetooth values
    bluetoothDevice,
    isBluetoothConnected,
    bluetoothSerialOutput,
    connectBluetooth,
    disconnectBluetooth,
    sendWiFiCredentialsBluetooth,
    serialData,
    startConfigurePort,
    isConfiguring,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
